import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import React from 'react';
import { useForm } from 'react-hook-form';
import { connect } from 'react-redux';
import * as yup from 'yup';
import { EditOrganizacionDTO } from '../../../api/organizaciones/editOrganizacion';
import { ReactComponent as IconEdit } from '../../../assets/moreMenu/edit.svg';
import renderWhen from '../../../helpers/renderWhen';
import useValidatorAPI from '../../../hooks/useValidatorAPI';
import {
  getOrganizacion,
  resetOrganizacion,
} from '../../../store/organizaciones/organizacionModule';
import {
  selectEditOrganizacion,
  selectEditOrganizacionActive,
  selectEditOrganizacionErrors,
  selectEditOrganizacionLoading,
} from '../../../store/organizaciones/selectors';
import {
  hide,
  editOrganizacion,
} from '../../../store/organizaciones/editOrganizacionModule';
import { AppDispatch, RootState } from '../../../store/store';
import { APIError } from '../../../types/api/api';
import { Organizacion } from '../../../types/organizaciones/organizacion';
import { UUID } from '../../../types/standard';
import Button from '../../common/Buttons/Button/Button';
import ButtonsGroup from '../../common/Buttons/ButtonsGroup/ButtonsGroup';
import GlobalError from '../../common/Form/GlobalError/GlobalError';
import ValidatedInput from '../../common/Form/ValidatedInput/ValidatedInput';
import FormLayout from '../../common/Layouts/FormLayout/FormLayout';
import Loading from '../../common/Loading/Loading';
import Modal from '../../common/Modal/Modal';
import styles from '../changeActions.module.scss';
import { t } from '../../../i18n';

const changeNombreOrganizacionSchema = yup
  .object()
  .shape({
    nombre: yup.string().required(t('ValidarIntroduceElNombre')),
  })
  .defined();

interface ChangeNombreOrganizacionProps {
  organizacion: Organizacion;
  serverErrors: APIError | null;
  active: boolean;
  loading: boolean;
  closeModal: () => void;
  editOrganizacion: (x: EditOrganizacionDTO) => void;
}

const ChangeNombreOrganizacion: React.FC<ChangeNombreOrganizacionProps> = ({
  organizacion,
  serverErrors,
  active,
  loading,
  closeModal,
  editOrganizacion,
}) => {
  const { t } = useTranslation();
  const {
    handleSubmit,
    register,
    formState: { errors },
    setError,
    formState,
  } = useForm<EditOrganizacionDTO>({
    mode: 'onSubmit',
    resolver: yupResolver(changeNombreOrganizacionSchema),
  });

  const [globalError] = useValidatorAPI(serverErrors, setError, formState);

  // 'active' controls if the modal is open or close
  if (!active) return null;

  const changeNombreOrganizacionSubmit = (data: EditOrganizacionDTO) => {
    data.id = organizacion.id;
    editOrganizacion(data);
    closeModal();
  };

  return (
    <>
      {organizacion && (
        <Modal variant="large" closeModal={closeModal}>
          <div className={styles.container}>
            <h1 className={styles.title}>
              <IconEdit className={`${styles.icon} ${styles.isIconEdit}`} />
              {t('ComponentsPopupActionsChangeNombreOrganizacionCambiarNombre')}
            </h1>
            <div className={styles.appointmentWrapper}>
              <FormLayout
                variant={'profile'}
                onSubmit={handleSubmit(changeNombreOrganizacionSubmit)}
              >
                <ValidatedInput
                  schema={changeNombreOrganizacionSchema}
                  errors={errors}
                  label={t(
                    'ComponentsPopupActionsChangeNombreOrganizacionNuevoNombre',
                  )}
                  input={{
                    ...register('nombre'),
                    name: 'nombre',
                    type: 'text',
                    defaultValue: organizacion.nombre,
                  }}
                ></ValidatedInput>
                <ButtonsGroup variant="profile">
                  <React.Fragment>
                    <Button
                      type="button"
                      variant="negative"
                      onClick={closeModal}
                    >
                      {t('CommonCerrar')}
                    </Button>
                    <Button variant="positive">{t('CommonGuardar')}</Button>
                  </React.Fragment>
                </ButtonsGroup>
              </FormLayout>
            </div>
            {/* Server-side global error */}
            {globalError && <GlobalError message={globalError} />}

            {loading && <Loading />}
          </div>
        </Modal>
      )}
    </>
  );
};

const ConnectedChangeNombreOrganizacion = connect(
  (state: RootState) => ({
    organizacion: selectEditOrganizacion(state),
    active: selectEditOrganizacionActive(state),
    loading: selectEditOrganizacionLoading(state),
    serverErrors: selectEditOrganizacionErrors(state),
  }),
  (dispatch: AppDispatch) => ({
    loadOrganizacion: (payload: UUID) => dispatch(getOrganizacion(payload)),
    resetOrganizacion: () => dispatch(resetOrganizacion()),
    editOrganizacion: (payload: EditOrganizacionDTO) =>
      dispatch(editOrganizacion(payload)),
    closeModal: () => dispatch(hide()),
  }),
)(ChangeNombreOrganizacion);

export default renderWhen(
  ConnectedChangeNombreOrganizacion,
  selectEditOrganizacionActive,
);
